import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import type { InvitationRecord } from '@/domain/teamTypes';
import { useAuth } from '@/hooks/useAuth';
import { pb } from '@/lib/pocketbase';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export function PendingInvitations() {
  const { user } = useAuth();
  const [invitations, setInvitations] = useState<InvitationRecord[] | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const records = await pb.collection('invitations').getFullList<InvitationRecord>({
          filter: `email = "${user.email}" && status = "pending"`,
          expand: 'team',
        });
        setInvitations(records);
      } catch {
        setInvitations([]);
      }
    })();
  }, [user]);

  const respond = async (invitation: InvitationRecord, status: 'accepted' | 'declined') => {
    setPendingId(invitation.id);
    try {
      await pb.collection('invitations').update(invitation.id, { status });
      setInvitations((prev) => prev?.filter((i) => i.id !== invitation.id) ?? null);
      toast.success(status === 'accepted' ? 'Invitation acceptée.' : 'Invitation refusée.');
    } catch {
      toast.error("Impossible de répondre à l'invitation.");
    } finally {
      setPendingId(null);
    }
  };

  if (!invitations || invitations.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {invitations.map((invitation) => (
        <Card key={invitation.id} className="border-primary bg-accent/40 py-4">
          <CardContent className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm font-semibold text-primary">
              Invitation en attente — {invitation.expand?.team?.name ?? 'une équipe'}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-primary text-primary hover:bg-primary/10"
                disabled={pendingId === invitation.id}
                onClick={() => respond(invitation, 'declined')}
              >
                Refuser
              </Button>
              <Button
                size="sm"
                disabled={pendingId === invitation.id}
                onClick={() => respond(invitation, 'accepted')}
              >
                Accepter
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
